import type { MembershipRules } from '@/api/Membership'
import type { ReserveTicketPayload } from '@/api/types/payloads'
import type { EventDetails, EventWithConfig, LinkedTG } from '@/api/types/processedEntities'
import { cartParams } from '@/helpers/BuyMembershipHelpers'
import { getMembershipConfigProperty } from '@/helpers/Config'
import { mapEntries } from '@/helpers/DictHelpers'
import { environment, timezone } from '@/helpers/Environment'
import { groupBy, indexBy } from '@/helpers/IndexHelpers'
import { reserveTickets } from '@/state/Reserve'
import type { UntimedItem } from '@/store/CartItem'
import store from '@/store/store'
import { TixTime } from '@/TixTime/TixTime'

export interface MembershipSummary {
  benefits: MemberBenefits
  validFrom: TixTime
  validTo: TixTime
}

/**
 * @deprecated Use MembershipWithEntity. Don't mix raw entities with processed data.
 */
export type DeprecatedMembership = MembershipSummary & MembershipEntity

export interface MembershipWithEntity extends MembershipSummary {
  name?: string
  ticketGroup: LinkedTG
  eventTemplate: EventWithConfig
  entity: MembershipEntity
}

/**
 * @deprecated Use MembershipWithEntity
 */
export type MembershipEntityAndSummary = MembershipEntity & MembershipSummary

/**
 * @deprecated This doesn't support multi-memberships
 */
export function currentMembership(): MembershipSummary | void {
  const now = new TixTime(undefined, timezone)
  return membershipOnDate(now)
}

/**
 * @deprecated This doesn't support multi-memberships
 */
export function membershipOnDate(date: TixTime): MembershipSummary | void {
  const memberships = allMemberships()
  return findMembershipOnDate(memberships, date)
}

/**
 * @deprecated This doesn't support multi-memberships
 */
export function purchasedMembershipOnDate(date: TixTime): MembershipSummary | void {
  const memberships = purchasedMemberships()
  return findMembershipOnDate(memberships, date)
}

function findMembershipOnDate(memberships: MembershipSummary[], date: TixTime): MembershipSummary | void {
  return memberships.find((membership) => {
    const from = membership.validFrom
    const to = membership.validTo
    return date.isBetween(from, to, 'day', '[]')
  })
}

export function inCartMembership(): MembershipSummary | void {
  const gifting = store.getters['Cart/giftee']
  const items = store.getters['Cart/cartItems']
  const item = items.find((item) => item.isMembershipEvent)

  if (item && !gifting) {
    return cartItemToMembership(item)
  }
}

/**
 * @deprecated Just use the store.
 */
export function purchasedMemberships(): MembershipEntityAndSummary[] {
  return store.getters['Member/membershipsWithSummaries']
}

/**
 * Includes the most recent membership from each membership program, including expired ones.
 */
export function mostRecentMemberships(): MembershipWithEntity[] {
  const memberships = store.getters['Member/memberships'] as MembershipWithEntity[]

  const result = {}
  for (const membership of memberships) {
    const now = new TixTime(null, timezone)
    // Ignore future memberships
    if (membership.validFrom.isAfter(now)) continue
    const programId = membership.entity.membership_program_id ?? 'default'
    // Get the most recent membership for each membership program
    if (!result[programId] || membership.validTo.isAfter(result[programId].validTo)) {
      result[programId] = membership
    }
  }
  return Object.values(result)
}

/**
 * Includes the _active_ membership from each membership program.
 *
 * Expired memberships are excluded.
 */
export function currentMemberships(): MembershipWithEntity[] {
  const memberships = store.getters['Member/memberships'] as MembershipWithEntity[]
  const now = new TixTime(undefined, timezone)
  return memberships.filter((membership) => {
    return now.isBefore(membership.validTo) && now.isAfter(membership.validFrom)
  })
}

/**
 * @deprecated This doesn't support multi-memberships
 */
export function hasCurrentPurchasedMembership(): boolean {
  const now = new TixTime(undefined, timezone)
  return purchasedMembershipOnDate(now) ? true : false
}

export function isCurrentMember(): boolean {
  return currentMemberships().length > 0
}

export function isCurrentOrPastMember(): boolean {
  const memberships = purchasedMemberships()
  return memberships.length > 0
}

function allMemberships(): MembershipSummary[] {
  const inCart = inCartMembership()
  // Always prioritise an in-cart membership over any purchased memberships so that the customer can not unnecessarily
  // purchase a membership in order to get benefits that they already have.
  return inCart ? [inCart] : purchasedMemberships()
}

function cartItemToMembership(cartItem: UntimedItem): MembershipSummary | void {
  const rules = membershipRules()

  if (rules) {
    const limits: MemberBenefits = {}

    for (const lineItem of cartItem.types) {
      // cartItem can contain ticket types where type.group.handler === 'codes' which are not relevant to member benefits.
      if (lineItem.group.handler === 'membership') {
        const name = rules[lineItem.group.id][lineItem.type.id].member_event_ticket_type
        if (limits[name]) {
          limits[name] += lineItem.ticketCount
        } else {
          limits[name] = lineItem.ticketCount
        }
      }
    }

    return {
      benefits: limits,
      validTo: cartItem.validTo,
      validFrom: cartItem.validFrom,
    }
  }
}

function membershipRules(): Dict<Dict<MembershipRuleEntity>> | void {
  const rules = store.state['membershipRules'] as MembershipRuleEntity[]
  if (rules.length) {
    const byGroup = groupBy('ticket_group_id', rules)
    return mapEntries(byGroup, (group) => indexBy('ticket_type_id', group))
  }
}

export function addMembershipToCart(
  event: EventDetails,
  membershipRules: MembershipRules,
  selectedLevel: string,
  promoCodes: Array<string | null | undefined>,
  tickets: ReserveTicketPayload[],
  surveyAnswers: Dict<Primitive>,
  autoRenew: boolean,
  codes?: string[],
): Promise<void> {
  store.commit('Cart/autoRenewMembership', autoRenew)
  // Ensure the membershipRules are available to create a membership entity from an in-cart membership.
  // @see cartItemToMembership()
  store.commit('membershipRules', membershipRules.rules)
  const changes = cartParams(event, selectedLevel, surveyAnswers, promoCodes.filter((code) => code != null) as string[])
  changes.add = tickets
  if (codes) {
    changes.preReservePromoCodes = { codes }
  }
  return reserveTickets(changes)
}

export function allowMemberReserveBeyondTerm(): boolean {
  const reserveBeyondTerm = environment.config.member_reserve_session_beyond_term
  const autoRenew = store.state['Cart'].autoRenewMembership
  return reserveBeyondTerm === 'always' || (reserveBeyondTerm === 'if_autorenew' && autoRenew)
}

export function membershipIsRenewable(membership: MembershipSummary, group: LinkedTG, event: EventWithConfig): boolean {
  const allowedDate = membershipRenewableFrom(membership, group, event)
  const now = new TixTime(null, timezone)
  return allowedDate.isSameOrBefore(now)
}

export function membershipRenewableFrom(
  membership: MembershipSummary,
  group: LinkedTG,
  event: EventWithConfig,
): TixTime {
  const config = getMembershipConfigProperty('renewal', 'allowed_period', group, event) ?? null
  if (config !== null) {
    return membership.validTo.subtract(config, 'days')
  }
  // Membership is renewable from today if the config is not set
  return new TixTime(null, timezone)
}
